import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useAuth } from "../../utils/auth";
import { canViewTickets } from "../../utils/roles";
import { fetchTicketProblemTypes, TICKET_STATUSES } from "../../api/TicketAPIs";
import { fetchTicketAnalytics } from "../../api/ReplacementAPIs";
import { resolveDateRange } from "../../utils/analyticsDates";

const PRESETS = [
  { id: "today", label: "اليوم" },
  { id: "thisWeek", label: "الأسبوع" },
  { id: "thisMonth", label: "الشهر" },
  { id: "custom", label: "مخصص" },
];

const Metric = ({ label, value }) => (
  <div className="bg-white rounded-2xl p-4 shadow-sm border border-white">
    <p className="text-sm text-gray-500">{label}</p>
    <p className="text-2xl font-bold text-gray-900 mt-1">{value ?? 0}</p>
  </div>
);

const TicketAnalyticsPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const canView = canViewTickets(authState.role);
  const [preset, setPreset] = useState("thisMonth");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [problemTypeId, setProblemTypeId] = useState("");
  const [status, setStatus] = useState("");
  const [governorate, setGovernorate] = useState("");
  const [ticketPage, setTicketPage] = useState(0);
  const [replacementPage, setReplacementPage] = useState(0);

  const range = useMemo(
    () => resolveDateRange(preset === "custom" ? "custom" : preset, customFrom, customTo),
    [preset, customFrom, customTo]
  );

  const { data: problemTypes = [] } = useQuery({
    queryKey: ["ticketProblemTypes", "active"],
    queryFn: () => fetchTicketProblemTypes(true),
    enabled: canView,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ["ticketAnalytics", range.startDate, range.endDate, problemTypeId, status, governorate, ticketPage, replacementPage],
    queryFn: () => fetchTicketAnalytics({
      fromUtc: range.startDate,
      toUtc: range.endDate,
      problemTypeId: problemTypeId || undefined,
      status: status || undefined,
      governorate: governorate || undefined,
      ticketPage,
      replacementPage,
      pageSize: 20,
    }),
    enabled: canView && !!range.startDate && !!range.endDate,
  });

  if (!canView) return null;

  const tickets = data?.tickets?.items || [];
  const replacements = data?.replacements?.items || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      <div className="max-w-6xl mx-auto space-y-4">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => navigate("/tickets")} className="p-2 rounded-xl bg-white shadow">
            <ChevronRight size={22} />
          </button>
          <h1 className="text-2xl md:text-4xl font-bold text-gray-800">تحليلات التذاكر</h1>
        </div>

        <div className="bg-white rounded-2xl p-4 flex flex-wrap gap-2">
          {PRESETS.map((item) => (
            <button key={item.id} type="button" onClick={() => { setPreset(item.id); setTicketPage(0); setReplacementPage(0); }} className={`px-3 py-1.5 rounded-full text-sm font-bold ${preset === item.id ? "bg-indigo-600 text-white" : "bg-gray-100"}`}>
              {item.label}
            </button>
          ))}
          {preset === "custom" && (
            <>
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="border rounded-xl px-2 py-1" />
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="border rounded-xl px-2 py-1" />
            </>
          )}
          <select value={problemTypeId} onChange={(e) => { setProblemTypeId(e.target.value); setTicketPage(0); }} className="border rounded-xl px-2 py-1">
            <option value="">كل الأسباب</option>
            {problemTypes.map((reason) => <option key={reason.id} value={reason.id}>{reason.name}</option>)}
          </select>
          <select value={status} onChange={(e) => { setStatus(e.target.value); setTicketPage(0); }} className="border rounded-xl px-2 py-1">
            <option value="">كل الحالات</option>
            {TICKET_STATUSES.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
          <select value={governorate} onChange={(e) => { setGovernorate(e.target.value); setTicketPage(0); }} className="border rounded-xl px-2 py-1">
            <option value="">كل المحافظات</option>
            {(data?.governorates || []).map((name) => <option key={name} value={name}>{name}</option>)}
          </select>
        </div>

        {isLoading && <p>جاري التحميل...</p>}
        {error && <p className="text-red-600">{error.message}</p>}

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Metric label="التذاكر" value={data.totalTickets} />
              <Metric label="مفتوحة" value={data.openTickets} />
              <Metric label="تم الحل" value={data.resolvedTickets} />
              <Metric label="طلبات أصلية متأثرة" value={data.distinctOriginalOrders} />
              <Metric label="طلبات الاستبدال" value={data.replacementOrders} />
              <Metric label="كمية قطع الاستبدال" value={data.replacementItemQuantity} />
              <Metric label="بنود الاستبدال" value={data.replacementLineCount} />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <ChartCard title="التذاكر حسب السبب" data={data.ticketsByProblemReason} />
              <ChartCard title="الاستبدال حسب السبب" data={data.replacementsByProblemReason} />
              <ChartCard title="المشاكل حسب المحافظة" data={data.problemsByGovernorate} />
            </div>

            <section className="bg-white rounded-2xl p-4">
              <h2 className="font-bold mb-2">التذاكر</h2>
              <table className="w-full text-sm">
                <tbody>
                  {tickets.map((row) => (
                    <tr key={row.id} className="border-t">
                      <td className="py-2">#{row.id}</td>
                      <td>طلب {row.orderId}</td>
                      <td>{row.status}</td>
                      <td>{row.governorate}</td>
                      <td>{row.customerName}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pager page={ticketPage} total={data.tickets?.totalCount || 0} onChange={setTicketPage} />
            </section>

            <section className="bg-white rounded-2xl p-4">
              <h2 className="font-bold mb-2">طلبات الاستبدال</h2>
              <table className="w-full text-sm">
                <tbody>
                  {replacements.map((row) => (
                    <tr key={row.replacementOrderId} className="border-t">
                      <td className="py-2">#{row.replacementOrderId}</td>
                      <td>أصل {row.originalOrderId}</td>
                      <td>{row.problemReason}</td>
                      <td>كمية {row.quantity}</td>
                      <td>{row.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pager page={replacementPage} total={data.replacements?.totalCount || 0} onChange={setReplacementPage} />
            </section>
          </>
        )}
      </div>
    </div>
  );
};

const ChartCard = ({ title, data }) => (
  <div className="bg-white rounded-2xl p-4 h-64">
    <h2 className="font-bold mb-2">{title}</h2>
    <ResponsiveContainer width="100%" height="85%">
      <BarChart data={data || []}>
        <XAxis dataKey="name" hide />
        <YAxis allowDecimals={false} width={28} />
        <Tooltip />
        <Bar dataKey="count" fill="#4f46e5" />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

const Pager = ({ page, total, onChange }) => (
  <div className="flex justify-between text-sm mt-2">
    <span>{total}</span>
    <div className="flex gap-2">
      <button type="button" disabled={page === 0} onClick={() => onChange(page - 1)} className="px-2 py-1 bg-gray-100 rounded disabled:opacity-40">السابق</button>
      <button type="button" disabled={(page + 1) * 20 >= total} onClick={() => onChange(page + 1)} className="px-2 py-1 bg-gray-100 rounded disabled:opacity-40">التالي</button>
    </div>
  </div>
);

export default TicketAnalyticsPage;
